import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { sourceColor } from './colors';
import styles from './style.module.css';

/*
 * Donut + legend for the games funding split (free daily allowance vs games
 * purchased/earned by spending). Driven entirely by the payload's slices, so a
 * funding source the backend hasn't classified yet still renders as its own
 * segment instead of vanishing or being lumped in with another.
 */

type Slice = { key: string; name: string; value: number; pct: number };

const numFmt = new Intl.NumberFormat('en-US');

type TooltipEntry = { name?: string; value?: number; payload?: Slice };

function SliceTooltip({ active, payload }: { active?: boolean; payload?: TooltipEntry[] }) {
  const entry = payload?.[0];
  if (!active || !entry) return null;
  const pct = entry.payload?.pct;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipName}>{entry.name}</div>
      <div className={styles.tooltipValue}>
        {numFmt.format(entry.value ?? 0)} games{pct != null && ` · ${pct}%`}
      </div>
    </div>
  );
}

export function SourceSplitDonut({ slices, total }: { slices: Slice[]; total: number }) {
  if (slices.length === 0 || total <= 0) {
    return <div className={styles.empty}>No games played yet.</div>;
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.chart}>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={58} outerRadius={88} paddingAngle={2} stroke="none">
              {slices.map((s, i) => (
                <Cell key={s.key} fill={sourceColor(s.key, i)} />
              ))}
            </Pie>
            <Tooltip content={<SliceTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className={styles.center}>
          <div className={styles.centerValue}>{numFmt.format(total)}</div>
          <div className={styles.centerLabel}>games</div>
        </div>
      </div>

      <ul className={styles.legend}>
        {slices.map((s, i) => (
          <li key={s.key} className={styles.legendRow}>
            <span className={styles.swatch} style={{ background: sourceColor(s.key, i) }} />
            <span className={styles.legendName}>{s.name}</span>
            <span className={styles.legendPct}>{s.pct}%</span>
            <span className={styles.legendValue}>{numFmt.format(s.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
